import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar,
  ArrowRight,
  ArrowLeft,
  Home,
  Lock,
  Sparkles,
  Upload,
  X,
  Edit3,
  MousePointer,
  FileText,
  Users,
  GraduationCap,
  Play,
  Pause,
  Volume2,
  Image as ImageIcon,
  Armchair,
  Quote
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin, EditableText } from '../AdminContentEditor';

// Assets
const WBS_LOGO = 'https://customer-assets.emergentagent.com/job_caf6c523-32ed-44de-aaab-8552e94ae126/artifacts/95iizd24_transparent-fortheminds%20%281%29.png';

const API_URL = window.location.origin;

// Image Lightbox Modal
const ImageLightbox = ({ src, alt, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
      >
        <X className="w-6 h-6" />
      </button>
      <motion.img
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        src={src}
        alt={alt}
        className="max-w-full max-h-[90vh] object-contain rounded-lg"
        onClick={(e) => e.stopPropagation()}
      />
    </motion.div>
  );
};

// Helper to check if URL is a video
const isVideoUrl = (url) => {
  if (!url) return false;
  const ext = url.split('.').pop()?.toLowerCase();
  return ['mp4', 'webm', 'mov', 'avi'].includes(ext);
};

// Helper to check if URL is a GIF
const isGifUrl = (url) => {
  if (!url) return false;
  return url.toLowerCase().endsWith('.gif');
};

// Default images that persist across deployments
const DEFAULT_IMAGES = {
  'whiteboard_photo': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/wd3vxgon_IMG_2753%202.jpg',
  'feature1_editing-demo': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/dqwc242u_inlineediting.gif',
  'feature2_cursor-demo-1': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/fzrb7rdg_cursor1.gif',
  'feature2_cursor-demo-2': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/yi7jyvpv_cursor2.gif',
  'workshop_event-photo-top-left': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/4pf5cqzm_gpxjniud_unnamed%20%282%29.png',
  'workshop_event-gif-top-right': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/8iz1tx8k_eventgif%20top.gif',
  'workshop_event-photo-middle': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/oikk66mo_event%20photo%20middle.png',
  'workshop_event-gif-bottom': 'https://customer-assets.emergentagent.com/job_70426e48-c2cc-4112-a300-074b3c8677c3/artifacts/wiwguih5_event%20gif%20bottom.gif',
};

// Editable Media Component with support for images, GIFs, and videos
const EditableImage = ({ sectionId, imageKey, caption, className = '', aspectRatio = 'aspect-video' }) => {
  const { isAdmin, isEditMode, updateSectionContent, sectionContent } = useAdmin();
  const [uploading, setUploading] = useState(false);
  const [localImage, setLocalImage] = useState(null);
  const [showLightbox, setShowLightbox] = useState(false);
  
  const storageKey = `dd_frontrow_image_${sectionId}_${imageKey}`;
  const defaultImageKey = `${sectionId}_${imageKey}`;
  const defaultImage = DEFAULT_IMAGES[defaultImageKey] || null;
  
  useEffect(() => {
    // Check backend content first
    const sectionData = sectionContent[`frontrow_${sectionId}`];
    if (sectionData && (sectionData[imageKey] || sectionData.custom_fields?.[imageKey])) {
      const savedUrl = sectionData[imageKey] || sectionData.custom_fields?.[imageKey];
      // Skip if it's an empty string, cleared, or a /api/uploads/ URL (these don't persist across deploys)
      if (savedUrl && savedUrl.trim() !== '' && !savedUrl.includes('/api/uploads/')) {
        setLocalImage(savedUrl);
        return;
      }
    }
    // Fallback to localStorage
    const saved = localStorage.getItem(storageKey);
    if (saved && saved.trim() !== '' && !saved.includes('/api/uploads/')) {
      setLocalImage(saved);
      return;
    }
    // Fallback to default image
    if (defaultImage) {
      setLocalImage(defaultImage);
    }
  }, [storageKey, sectionContent, sectionId, imageKey, defaultImage]);
  
  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Get auth token
      const sessionToken = localStorage.getItem('auth_session_token');
      const headers = {};
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      const response = await fetch(`${API_URL}/api/admin/upload`, { 
        method: 'POST', 
        headers,
        credentials: 'include',
        body: formData 
      });
      
      if (response.ok) {
        const data = await response.json();
        const mediaUrl = data.url || data.file_url;
        const fullUrl = mediaUrl.startsWith('http') ? mediaUrl : `${API_URL}${mediaUrl}`;
        
        // Save to backend
        await updateSectionContent(`frontrow_${sectionId}`, { [imageKey]: fullUrl });
        localStorage.setItem(storageKey, fullUrl);
        setLocalImage(fullUrl);
        console.log(`[EditableMedia] Saved to backend: frontrow_${sectionId}.${imageKey}`);
        setUploading(false);
        return;
      }
    } catch (error) {
      console.log('Backend upload failed, using localStorage fallback:', error);
    }
    
    // Fallback to localStorage with data URL
    const reader = new FileReader();
    reader.onload = async (event) => {
      const dataUrl = event.target?.result;
      localStorage.setItem(storageKey, dataUrl);
      // Also try to save to backend
      await updateSectionContent(`frontrow_${sectionId}`, { [imageKey]: dataUrl });
      setLocalImage(dataUrl);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const clearMedia = async (e) => {
    // Prevent event bubbling to parent onClick (lightbox)
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Clear from localStorage
    localStorage.removeItem(storageKey);
    
    // Clear from backend - set to empty string (null doesn't always work)
    try { 
      await updateSectionContent(`frontrow_${sectionId}`, { [imageKey]: '' }); 
      console.log(`[EditableMedia] Cleared from backend: frontrow_${sectionId}.${imageKey}`);
    } catch (e) {
      console.log('Failed to clear from backend:', e);
    }
    
    // Clear local state
    setLocalImage(null);
  };
  
  // Render the media (image, GIF, or video)
  const renderMedia = () => {
    const isVideo = isVideoUrl(localImage);
    
    if (isVideo) {
      return (
        <video 
          src={localImage} 
          autoPlay 
          loop 
          muted 
          playsInline
          className="w-full h-auto object-contain"
        />
      );
    }
    
    // Images and GIFs render the same way
    return <img src={localImage} alt={caption} className="w-full h-auto object-contain" />;
  };
  
  if (localImage) {
    const isVideo = isVideoUrl(localImage);
    
    return (
      <>
        <div 
          className={`relative rounded-2xl overflow-hidden bg-gray-100 ${className} ${!isEditMode && !isVideo ? 'cursor-zoom-in group' : ''}`}
          onClick={() => !isEditMode && !isVideo && setShowLightbox(true)}
        >
          {renderMedia()}
          {!isEditMode && !isVideo && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
                <ArrowRight className="w-5 h-5 text-gray-700 rotate-[-45deg]" />
              </div>
            </div>
          )}
          {isAdmin && isEditMode && (
            <button 
              onClick={(e) => clearMedia(e)} 
              className="absolute top-2 right-2 p-2 rounded-full bg-red-500 text-white hover:bg-red-600 z-20"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        {showLightbox && !isVideo && <ImageLightbox src={localImage} alt={caption} onClose={() => setShowLightbox(false)} />}
      </>
    );
  }
  
  return (
    <div className={`bg-gray-100 rounded-2xl border-2 border-dashed border-gray-300 flex flex-col items-center justify-center min-h-[200px] ${className}`}>
      {isAdmin && isEditMode ? (
        <label className="cursor-pointer flex flex-col items-center p-6 w-full h-full justify-center hover:bg-gray-200 transition-colors rounded-2xl">
          <input 
            type="file" 
            accept="image/*,video/mp4,video/webm,video/quicktime,.gif" 
            onChange={handleMediaUpload} 
            className="hidden" 
          />
          {uploading ? (
            <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Upload className="w-10 h-10 text-purple-500 mb-2" />
              <span className="text-sm text-purple-600 font-medium">Upload Image, GIF, or Video</span>
              <span className="text-xs text-gray-500 mt-1">Videos autoplay on loop</span>
              <span className="text-xs text-gray-600 mt-1 text-center">{caption}</span>
            </>
          )}
        </label>
      ) : (
        <>
          <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
          <span className="text-sm text-gray-600 text-center font-medium">{caption}</span>
        </>
      )}
    </div>
  );
};

// Small Image Upload (for logos)
const SmallImageUpload = ({ sectionId, imageKey, caption, size = 'h-16' }) => {
  const { isAdmin, isEditMode } = useAdmin();
  const [localImage, setLocalImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const storageKey = `dd_frontrow_image_${sectionId}_${imageKey}`;
  
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) setLocalImage(saved);
  }, [storageKey]);
  
  const handleUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = (event) => {
      localStorage.setItem(storageKey, event.target?.result);
      setLocalImage(event.target?.result);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };
  
  const clearImage = () => {
    localStorage.removeItem(storageKey);
    setLocalImage(null);
  };
  
  if (localImage) {
    return (
      <div className="relative inline-block">
        <img src={localImage} alt={caption} className={`${size} object-contain`} />
        {isAdmin && isEditMode && (
          <button onClick={clearImage} className="absolute -top-1 -right-1 p-1 rounded-full bg-red-500 text-white">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }
  
  if (isAdmin && isEditMode) {
    return (
      <label className={`cursor-pointer ${size} w-24 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-300 flex flex-col items-center justify-center transition-colors`}>
        <input type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        {uploading ? (
          <div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" />
        ) : (
          <>
            <Upload className="w-5 h-5 text-gray-400" />
            <span className="text-[10px] text-gray-400">{caption}</span>
          </>
        )}
      </label>
    );
  }
  
  return (
    <div className={`${size} w-24 rounded-lg bg-gray-100 flex items-center justify-center`}>
      <span className="text-[10px] text-gray-400">{caption}</span>
    </div>
  );
};

// LMU Group Photo with Lightbox
const LMUGroupPhoto = () => {
  const [showLightbox, setShowLightbox] = useState(false);
  const imageSrc = "/lmu-students.jpg";
  
  return (
    <>
      <div 
        className="rounded-xl overflow-hidden border border-gray-200 shadow-sm cursor-zoom-in group relative"
        onClick={() => setShowLightbox(true)}
      >
        <img 
          src={imageSrc} 
          alt="LMU Students Group Photo"
          className="w-full h-auto object-cover"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-2">
            <ArrowRight className="w-5 h-5 text-gray-700 rotate-[-45deg]" />
          </div>
        </div>
      </div>
      {showLightbox && (
        <ImageLightbox 
          src={imageSrc} 
          alt="LMU Students Group Photo" 
          onClose={() => setShowLightbox(false)} 
        />
      )}
    </>
  );
};

// Audio Upload with Quote
const AudioTestimonial = ({ index, sectionId }) => {
  const { isAdmin, isEditMode } = useAdmin();
  const [quote, setQuote] = useState('');
  const [studentName, setStudentName] = useState('');
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);
  
  const quoteKey = `dd_frontrow_quote_${sectionId}_${index}`;
  const audioKey = `dd_frontrow_audio_${sectionId}_${index}`;
  const nameKey = `dd_frontrow_name_${sectionId}_${index}`;
  
  useEffect(() => {
    const savedQuote = localStorage.getItem(quoteKey);
    const savedAudio = localStorage.getItem(audioKey);
    const savedName = localStorage.getItem(nameKey);
    if (savedQuote) setQuote(savedQuote);
    if (savedAudio) setAudioUrl(savedAudio);
    if (savedName) setStudentName(savedName);
  }, [quoteKey, audioKey, nameKey]);
  
  const handleQuoteChange = (e) => {
    setQuote(e.target.value);
    localStorage.setItem(quoteKey, e.target.value);
  };
  
  const handleNameChange = (e) => {
    setStudentName(e.target.value);
    localStorage.setItem(nameKey, e.target.value);
  };
  
  const handleAudioUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      localStorage.setItem(audioKey, event.target?.result);
      setAudioUrl(event.target?.result);
    };
    reader.readAsDataURL(file);
  };
  
  const togglePlay = () => {
    if (!audioUrl || !audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  
  const defaultName = `Student ${index + 1}`;
  
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-200">
      {/* Audio Player */}
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={togglePlay}
          disabled={!audioUrl}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            audioUrl 
              ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 cursor-pointer' 
              : 'bg-gray-100 text-gray-400'
          }`}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        
        <div className="flex-1">
          {isAdmin && isEditMode ? (
            <input
              type="text"
              value={studentName}
              onChange={handleNameChange}
              placeholder={defaultName}
              className="font-medium text-gray-900 text-sm bg-transparent outline-none border-b border-dashed border-purple-300 w-full"
            />
          ) : (
            <p className="font-medium text-gray-900 text-sm">{studentName || defaultName}</p>
          )}
          {audioUrl ? (
            <p className="text-xs text-green-600">Audio uploaded</p>
          ) : isAdmin && isEditMode ? (
            <label className="text-xs text-purple-600 cursor-pointer hover:underline">
              <input type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" />
              Upload audio
            </label>
          ) : (
            <p className="text-xs text-gray-400">Audio coming soon</p>
          )}
        </div>
        
        <Volume2 className="w-5 h-5 text-gray-400" />
      </div>
      
      {audioUrl && <audio ref={audioRef} src={audioUrl} onEnded={() => setIsPlaying(false)} />}
      
      {/* Quote Section */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-start gap-2">
          <Quote className="w-4 h-4 text-purple-400 flex-shrink-0 mt-1" />
          {isAdmin && isEditMode ? (
            <textarea
              value={quote}
              onChange={handleQuoteChange}
              placeholder="Enter student quote..."
              className="flex-1 text-sm text-gray-600 italic bg-transparent resize-none outline-none min-h-[60px]"
            />
          ) : (
            <p className="text-sm text-gray-600 italic">
              {quote || 'Quote coming soon...'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

// Hardcoded Audio Card with actual audio playback
const HardcodedAudioCard = ({ title, audioUrl, quote }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioRef = useRef(null);
  
  const togglePlay = () => {
    if (!audioRef.current || hasError) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.log('Audio play error:', err);
        setHasError(true);
      });
    }
    setIsPlaying(!isPlaying);
  };
  
  return (
    <div className="bg-white rounded-2xl p-5 border border-gray-200">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={togglePlay}
          className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
            hasError 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-purple-100 text-purple-600 hover:bg-purple-200 cursor-pointer'
          }`}
        >
          {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <div className="flex-1">
          <p className="font-medium text-gray-900 text-sm">{title}</p>
          <p className="text-xs text-green-600">Audio uploaded</p>
        </div>
        <Volume2 className="w-5 h-5 text-gray-400" />
      </div>
      
      <audio 
        ref={audioRef} 
        onEnded={() => setIsPlaying(false)}
        onError={() => setHasError(true)}
        preload="metadata"
      >
        <source src={audioUrl} type="audio/mp4" />
        <source src={audioUrl} type="audio/m4a" />
        <source src={audioUrl} type="audio/x-m4a" />
      </audio>
      
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-start gap-2">
          <Quote className="w-4 h-4 text-purple-400 flex-shrink-0 mt-1" />
          <p className="text-sm text-gray-600 italic">{quote}</p>
        </div>
      </div>
    </div>
  );
};

// Cloud Background Component (same as homepage)
const CloudBg = () => (
  <div className="absolute inset-0 overflow-hidden">
    {/* Base gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-white via-sky-50/50 to-violet-50/30" />
    
    {/* Large soft gradient orbs */}
    <motion.div 
      className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-sky-200/30 rounded-full blur-[100px]"
      animate={{ scale: [1, 1.1, 1], x: [0, 30, 0] }}
      transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
    />
    <motion.div 
      className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-violet-200/25 rounded-full blur-[80px]"
      animate={{ scale: [1.1, 1, 1.1], x: [0, -20, 0] }}
      transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
    />
    <motion.div 
      className="absolute bottom-0 left-1/3 w-[500px] h-[400px] bg-pink-100/30 rounded-full blur-[80px]"
      animate={{ scale: [1, 1.15, 1] }}
      transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut', delay: 5 }}
    />

    {/* Floating cloud shapes */}
    {[...Array(6)].map((_, i) => (
      <motion.div
        key={`cloud-${i}`}
        className="absolute rounded-full bg-white/60"
        style={{
          width: `${150 + Math.random() * 200}px`,
          height: `${80 + Math.random() * 100}px`,
          left: `${-5 + (i * 18)}%`,
          top: `${15 + Math.random() * 50}%`,
          filter: 'blur(20px)',
        }}
        animate={{
          x: [0, 50 + Math.random() * 30, 0],
          y: [0, -10 + Math.random() * 20, 0],
        }}
        transition={{
          duration: 25 + Math.random() * 15,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: i * 3,
        }}
      />
    ))}
  </div>
);

// Brain Food Section with Cloud Background
const BrainFoodSection = () => {
  const { isAdmin, isEditMode } = useAdmin();
  
  // Editable Brain Food Card
  const EditableBrainFoodCard = ({ cardId, defaultTitle, defaultDesc, icon: Icon, color, path }) => (
    <Link 
      to={path}
      className="block group relative overflow-hidden rounded-2xl md:rounded-3xl transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: `linear-gradient(135deg, ${color}15 0%, ${color}30 100%)`,
        border: `1px solid ${color}40`,
      }}
    >
      <div 
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `radial-gradient(circle at center, ${color}20 0%, transparent 70%)` }}
      />
      <div className="relative p-5 md:p-6">
        <div className="flex items-start gap-4">
          <div 
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: `${color}25` }}
          >
            <Icon className="w-6 h-6" style={{ color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-gray-900 mb-1">
              <EditableText sectionId="dd-month1-brainfood" field={`${cardId}-title`} defaultValue={defaultTitle} className="font-semibold text-gray-900" />
            </h4>
            <p className="text-sm text-gray-600">
              <EditableText sectionId="dd-month1-brainfood" field={`${cardId}-desc`} defaultValue={defaultDesc} className="text-sm text-gray-600" />
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors flex-shrink-0" />
        </div>
      </div>
    </Link>
  );

  return (
    <section className="py-16 px-4 relative overflow-hidden">
      {/* Cloud Background */}
      <CloudBg />
      
      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm text-pink-600 text-sm font-medium mb-4 shadow-sm">
              <Sparkles className="w-4 h-4" />
              <EditableText sectionId="dd-month1-brainfood" field="badge" defaultValue="Brain Food" className="text-pink-600 text-sm font-medium" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              <EditableText sectionId="dd-month1-brainfood" field="title" defaultValue="Resources & Prompts" className="text-2xl font-bold text-gray-900" />
            </h2>
            <p className="text-gray-600 max-w-lg mx-auto">
              <EditableText sectionId="dd-month1-brainfood" field="description" defaultValue="Resources and prompts used to build features mentioned in this month's documentation." className="text-gray-600" />
            </p>
          </div>
          
          <div className="space-y-4">
            <EditableBrainFoodCard 
              cardId="card1"
              defaultTitle="Content Management"
              defaultDesc="Make your website content editable without code. Prompts for inline editing and image uploads."
              icon={FileText}
              color="#FF007F"
              path="/resources/prompts/content-management"
            />
            <EditableBrainFoodCard 
              cardId="card2"
              defaultTitle="Custom Cursor Effects"
              defaultDesc="Add magical cursor trails and interactive effects to your website."
              icon={MousePointer}
              color="#00CED1"
              path="/resources/prompts/custom-cursor-effects"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Access Gate
const AccessGate = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-white">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md text-center">
        <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-6">
          <Lock className="w-10 h-10 text-gray-400" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Access Required</h2>
        <p className="text-gray-600 mb-8">You need to access the Front Row overview first.</p>
        <button onClick={() => navigate('/lab/frontrow/datadreaming')} className="w-full py-4 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors mb-4">
          Go to Front Row Overview
        </button>
        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 text-sm hover:text-gray-700">
          <Home className="w-4 h-4" /> Back to Data Dreaming
        </Link>
      </motion.div>
    </div>
  );
};

// Main Component
const DataDreamingMonth1 = () => {
  // No access gate - page is published but unlisted
  const hasAccess = true;
  const { isAdmin, isEditMode } = useAdmin();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/lab/frontrow/datadreaming" className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-700" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                <Armchair className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900">Month 1</span>
            </div>
          </div>
          <span className="text-sm text-gray-500">Data Dreaming</span>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 px-4 bg-gradient-to-b from-pink-50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-100 text-pink-600 text-sm font-medium mb-6">
              <Calendar className="w-4 h-4" /> 
              <EditableText sectionId="dd-month1-hero" field="badge" defaultValue="Month 1" className="text-pink-600 text-sm font-medium" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              <EditableText sectionId="dd-month1-hero" field="title" defaultValue="Building Data Dreaming" className="text-4xl md:text-5xl font-bold text-gray-900" />
            </h1>
            <p className="text-xl text-gray-700 mb-6">
              <EditableText sectionId="dd-month1-hero" field="subtitle" defaultValue="From whiteboard to working platform." className="text-xl text-gray-700" />
            </p>
            <p className="text-gray-600 max-w-2xl mx-auto">
              <EditableText sectionId="dd-month1-hero" field="description" defaultValue="Month 1 documents the first stage of building the Data Dreaming community platform using Emergent." className="text-gray-600" />
            </p>
          </motion.div>
        </div>
      </section>

      {/* Whiteboard */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              <EditableText sectionId="dd-month1-whiteboard" field="title" defaultValue="Whiteboard" className="text-2xl font-bold text-gray-900" />
            </h2>
            <p className="text-gray-700 mb-4">
              <EditableText sectionId="dd-month1-whiteboard" field="para1" defaultValue="Every system starts with structure." className="text-gray-700" />
            </p>
            <p className="text-gray-600 mb-4">
              <EditableText sectionId="dd-month1-whiteboard" field="para2" defaultValue="Before building the platform I began with a whiteboard session to outline the goals for the Data Dreaming community and sketch the initial wireframes for the site." className="text-gray-600" />
            </p>
            <p className="text-gray-600 mb-4">
              <EditableText sectionId="dd-month1-whiteboard" field="para3" defaultValue="The goal was to create a platform that could support resources, community experiments, and events while remaining flexible and easy to maintain." className="text-gray-600" />
            </p>
            <p className="text-gray-700 mb-8">
              <EditableText sectionId="dd-month1-whiteboard" field="para4" defaultValue="Once the structure was clear I began building the platform using Emergent." className="text-gray-700" />
            </p>
            <div className="max-w-2xl mx-auto">
              <EditableImage sectionId="whiteboard" imageKey="photo" caption="Whiteboard photo" aspectRatio="aspect-[16/10]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Inline Editing */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Edit3 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <span className="text-sm text-purple-600 font-medium">
                  <EditableText sectionId="dd-month1-feature1" field="label" defaultValue="Feature 1" className="text-sm text-purple-600 font-medium" />
                </span>
                <h2 className="text-2xl font-bold text-gray-900">
                  <EditableText sectionId="dd-month1-feature1" field="title" defaultValue="Inline Editing" className="text-2xl font-bold text-gray-900" />
                </h2>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              <EditableText sectionId="dd-month1-feature1" field="para1" defaultValue="Content can be edited directly on the page." className="text-gray-700" />
            </p>
            <p className="text-gray-600 mb-8">
              <EditableText sectionId="dd-month1-feature1" field="para2" defaultValue="Text can be updated and images replaced in place which makes maintaining the platform extremely simple." className="text-gray-600" />
            </p>
            <div className="max-w-2xl mx-auto">
              <EditableImage sectionId="feature1" imageKey="editing-demo" caption="Editing demo" aspectRatio="aspect-[16/10]" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cursor Effects */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-pink-100 flex items-center justify-center">
                <MousePointer className="w-5 h-5 text-pink-600" />
              </div>
              <div>
                <span className="text-sm text-pink-600 font-medium">
                  <EditableText sectionId="dd-month1-feature2" field="label" defaultValue="Feature 2" className="text-sm text-pink-600 font-medium" />
                </span>
                <h2 className="text-2xl font-bold text-gray-900">
                  <EditableText sectionId="dd-month1-feature2" field="title" defaultValue="Cursor Effects and Animations" className="text-2xl font-bold text-gray-900" />
                </h2>
              </div>
            </div>
            
            <p className="text-gray-600 mb-4">
              <EditableText sectionId="dd-month1-feature2" field="description" defaultValue="Small interaction details like cursor effects and animations help the platform feel more dynamic and exploratory." className="text-gray-600" />
            </p>
            <p className="text-gray-600 mb-8">
              <EditableText sectionId="dd-month1-feature2" field="description2" defaultValue="These visual touches create a more engaging experience that invites users to explore and interact with the content." className="text-gray-600" />
            </p>
            
            {/* Two image placeholders side by side */}
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
              <EditableImage sectionId="feature2" imageKey="cursor-demo-1" caption="Cursor effect example 1" />
              <EditableImage sectionId="feature2" imageKey="cursor-demo-2" caption="Cursor effect example 2" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Brain Food - Cloud Background */}
      <BrainFoodSection />

      {/* IRL Workshop */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">
                <EditableText sectionId="dd-month1-workshop" field="title" defaultValue="IRL Workshop" className="text-2xl font-bold text-gray-900" />
              </h2>
            </div>
            
            <p className="text-gray-700 mb-4">
              <EditableText sectionId="dd-month1-workshop" field="para1" defaultValue="After building the platform I tested it through a real event." className="text-gray-700" />
            </p>
            <p className="text-gray-600 mb-4">
              <EditableText sectionId="dd-month1-workshop" field="para2" defaultValue="I was invited to speak at a workshop in Los Angeles focused on exploring AI tools." className="text-gray-600" />
            </p>
            <p className="text-gray-600 mb-8">
              <EditableText sectionId="dd-month1-workshop" field="para3" defaultValue="The event page was designed to be mobile responsive so attendees could follow along directly from their phones." className="text-gray-600" />
            </p>
            
            {/* Women Soiree Cover Image */}
            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 flex items-center justify-center max-w-md mx-auto mb-8">
              <img src={WBS_LOGO} alt="Women Business Soirée" className="h-24 object-contain" />
            </div>
            
            {/* Row 1: 4 women photo + top gif side by side */}
            <div className="grid md:grid-cols-2 gap-6 mb-6 max-w-2xl mx-auto">
              <EditableImage sectionId="workshop" imageKey="event-photo-top-left" caption="Event photo" />
              <EditableImage sectionId="workshop" imageKey="event-gif-top-right" caption="Event GIF" />
            </div>
            
            {/* Row 2: Speaking photo in the middle */}
            <div className="max-w-xl mx-auto mb-6">
              <EditableImage sectionId="workshop" imageKey="event-photo-middle" caption="Speaking photo" />
            </div>
            
            {/* Row 3: Bottom event GIF */}
            <div className="max-w-xl mx-auto">
              <EditableImage sectionId="workshop" imageKey="event-gif-bottom" caption="Event GIF" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Month 2 Preview */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-200 text-gray-600 text-sm font-medium mb-4">
              <EditableText sectionId="dd-month1-month2" field="badge" defaultValue="Coming Soon" className="text-gray-600 text-sm font-medium" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <EditableText sectionId="dd-month1-month2" field="title" defaultValue="Month 2" className="text-2xl font-bold text-gray-900" />
            </h2>
            <p className="text-gray-600 max-w-md mx-auto">
              <EditableText sectionId="dd-month1-month2" field="description" defaultValue="Expanding the ecosystem and building new features based on community feedback." className="text-gray-600" />
            </p>
          </motion.div>
        </div>
      </section>

      {/* Front Row - Student Workshop (Hardcoded with Audio) */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            {/* Title with Icon */}
            <div className="flex items-center justify-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <GraduationCap className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Front Row - Student Workshop</h2>
            </div>
            
            {/* Group Photo */}
            <div className="max-w-xl mx-auto mb-6">
              <LMUGroupPhoto />
            </div>
            
            {/* Event Details */}
            <p className="text-gray-700 font-medium mb-1">February 25, 2026</p>
            <p className="text-gray-500 mb-8">Private University in Los Angeles, CA</p>
            
            {/* 2x2 Testimonial Grid - Hardcoded with Audio */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 max-w-2xl mx-auto">
              {/* Card 1: Community Building */}
              <HardcodedAudioCard 
                title="Community Building"
                audioUrl="https://customer-assets.emergentagent.com/job_frontrow-build/artifacts/l4veefr5_build%20communityLMU.m4a"
                quote="I learned how important it is to talk to your customers... & introduce a product/idea to where they gather."
              />
              
              {/* Card 2: Using Emergent */}
              <HardcodedAudioCard 
                title="Using Emergent"
                audioUrl="https://customer-assets.emergentagent.com/job_frontrow-build/artifacts/qbk2jl9x_emergentfeedbackLMU.m4a"
                quote="We're definitely going to use that for our agency and prompting our AI tech stack."
              />
              
              {/* Card 3: Endless Curiosity */}
              <HardcodedAudioCard 
                title="Endless Curiosity"
                audioUrl="https://customer-assets.emergentagent.com/job_frontrow-build/artifacts/1ofkr211_energyfeedbackLMU.m4a"
                quote="You spoke with such passion, energy and love for what you do, and as a Founder, I really appreciated that."
              />
              
              {/* Card 4: Problem Solving */}
              <HardcodedAudioCard 
                title="Problem Solving"
                audioUrl="https://customer-assets.emergentagent.com/job_frontrow-build/artifacts/yrm635ha_marriedtoproblemnotsolution.m4a"
                quote="Not being too attached to one specific solution... it will evolve over time."
              />
            </div>
            
            {/* Conclusion Note */}
            <p className="text-gray-700 border-l-4 border-purple-300 pl-4 py-2 bg-purple-50 rounded-r-lg text-left max-w-2xl mx-auto">
              Tested Front Row concept as a guest speaker.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto flex items-center justify-center">
          <Link to="/lab/frontrow/datadreaming" className="text-gray-500 text-sm hover:text-gray-600">
            Back to Front Row
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default DataDreamingMonth1;
